<template>
  <div class="flex w-full gap-8 pb-6">
    <div class="w-1/4 space-y-1">
      <h3 class="font-semibold text-dark-neutral-500">Specify the questions</h3>
      <p class="text-sm text-gray-600 font-light">
        Define each question you want to extract from the record.
      </p>
    </div>
    <div class="w-full">
      <div>
        <div class="w-full">
          <div class="w-full border rounded-lg border-mid-neutral-50">
            <div class="w-full text-sm py-7.5">
              <div class="pl-14 px-5">
                <div
                  class="px-3 grid grid-cols-12 font-medium text-mid-neutral-300 space-y-2.5"
                >
                  <div class="col-span-2">Question ID</div>
                  <div class="col-span-5">Question</div>
                  <div class="col-span-2 text-right">Extraction type</div>
                  <div class="col-span-3"></div>
                </div>

                <hr class="w-full border-mid-neutral-50" />
              </div>

              <Sortable
                :items="sortedAttributes"
                @item:moved="
                  ({ fractionalIndex, item }) =>
                    moveAttribute(item, fractionalIndex)
                "
              >
                <template #item="{ item, index, onDragEnd, onDragStart }">
                  <div
                    class="flex w-full items-center group px-3 hover:bg-light-neutral-300"
                  >
                    <div
                      :class="{
                        'invisible group-hover:visible':
                          !review.isPlanReadonly.value,
                        invisible: review.isPlanReadonly.value,
                      }"
                    >
                      <PlanLockGuard>
                        <button
                          class="py-1 text-secondary hover:brightness-125 text-mid-neutral-500"
                          :disabled="review.isPlanReadonly.value"
                          @click.stop="addAttributesStructure"
                        >
                          <PlusIcon class="size-5" />
                        </button>
                      </PlanLockGuard>
                    </div>

                    <SortableItem
                      :drag-handle-classes="
                        review.isPlanReadonly.value
                          ? 'invisible'
                          : 'invisible group-hover:visible'
                      "
                      class="w-full"
                      @dragstart="onDragStart"
                      @dragend="onDragEnd"
                    >
                      <AttributeStructureBuilder
                        :key="item.id"
                        :structure="item"
                        :question-id="index + 1"
                        @delete-attribute-structure="deleteAttributesStructure"
                      />
                    </SortableItem>
                  </div>
                </template>
              </Sortable>

              <div
                v-if="
                  review.entity.value.plan!.synthesisPlan!.attributesStructure
                    .length === 0
                "
                class="pl-14"
              >
                <PlanLockGuard>
                  <button
                    class="w-full max-w-sm text-sm font-medium flex items-center text-secondary-500 py-3 gap-1"
                    :disabled="review.isPlanReadonly.value"
                    @click="addAttributesStructure()"
                  >
                    <PlusIcon class="size-4" /> Add question
                  </button>
                </PlanLockGuard>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import AttributeStructureBuilder from './AttributeStructureBuilder.vue'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '../../../use-review'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { HttpException } from '@core/exceptions/http.exception'
import { errorMessage } from '@app/utils/error-message'
import Sortable from '@app/components/Global/Sortable/Sortable.vue'
import { AttributeStructure } from '@core/domain/models/data-extraction-plan-attribute'
import { computed } from 'vue'
import { generateKeyBetween } from 'fractional-indexing'
import { PlusIcon } from '@heroicons/vue/24/outline'
import SortableItem from '@app/components/Global/Sortable/SortableItem.vue'
import PlanLockGuard from '../../PlanLockGuard.vue'

const review = injectStrict(ReviewKey)
const loading = useLoading()
const snackbar = useSnackbar()
const sortedAttributes = computed(() =>
  review.entity.value.plan!.synthesisPlan!.attributesStructure.toSorted(
    (a, b) => {
      if (a.order === null && b.order === null) return 0
      if (a.order === null) return -1
      if (b.order === null) return 1
      return a.order > b.order ? 1 : -1
    },
  ),
)

async function deleteAttributesStructure(attributeStructureId: string) {
  try {
    loading.start()
    await review.deleteAttributeStructure(
      review.entity.value.id,
      attributeStructureId,
    )
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    loading.stop()
  }
}

async function addAttributesStructure() {
  try {
    loading.start()
    let order = 'a0'
    const lastAttribute = sortedAttributes.value.at(-1)
    if (lastAttribute) {
      order = generateKeyBetween(lastAttribute.order, null)
    }

    await review.addAttributeStructure(review.entity.value.id, {
      label: '',
      question: '',
      order,
    })

    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException

    snackbar.show(
      SnackbarState.ERROR,
      'An error occured during adding attribute structure',
    )
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    loading.stop()
  }
}

async function moveAttribute(attribute: AttributeStructure, order: string) {
  loading.start()
  try {
    await review.editAttributeStructure(review.entity.value.id, {
      ...attribute,
      order,
    })
  } finally {
    loading.stop()
  }
}
</script>
